import { Box } from '@mui/material';
import { ControlledShow } from 'framework/show';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { InterruptionCreatorField } from '../../../shared/field';
import InterruptionMessage from '../../../shared/field/InterruptionMessage';
import { openExternalLink } from '../../../shared/helpers/linkHelpers';
import { formatEmergencyTtl } from '../../../shared/helpers/emergencyInterruptionMappers';
import { InterruptionEmergencyCardView } from '../../../types';
import InterruptionsEmergencyCardActions from './InterruptionsEmergencyCardActions';
import InterruptionsEmergencyCardHeader from './InterruptionsEmergencyCardHeader';

type InterruptionsEmergencyCardProps = {
  item: InterruptionEmergencyCardView;
  onAccept: (id: string, teamsCallLink: string) => Promise<boolean>;
  onCancel: (id: string) => Promise<boolean>;
  onDismiss: (id: string) => Promise<boolean>;
  onExpire: (id: string) => void;
  onPostpone: (id: string) => Promise<boolean>;
};

const CARD_SX = {
  bgcolor: 'background.brandSubtle',
  borderRadius: 2,
  boxShadow: 4,
  px: 1.5,
  py: 1.25,
  position: 'relative',
  width: 360,
  zIndex: 1,
  border: 0.5,
} as const;

const InterruptionsEmergencyCard = ({
  item,
  onAccept,
  onCancel,
  onDismiss,
  onExpire,
  onPostpone,
}: InterruptionsEmergencyCardProps) => {
  const [now, setNow] = useState(() => moment.utc());
  const hasExpiredRef = useRef(false);
  const expiresAt = useMemo(() => moment.utc(item.expiresAtIso), [item.expiresAtIso]);
  const ttlMs = useMemo(() => expiresAt.diff(now, 'milliseconds'), [expiresAt, now]);
  const ttlLabel = useMemo(() => formatEmergencyTtl(ttlMs), [ttlMs]);
  const isExpired = ttlMs <= 0;
  const isUrgentTimer = !isExpired && ttlMs < 60 * 1000;

  useEffect(() => {
    const timer = window.setInterval(() => setNow(moment.utc()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    hasExpiredRef.current = false;
  }, [item.id, item.expiresAtIso]);

  useEffect(() => {
    if (!isExpired || hasExpiredRef.current) {
      return;
    }

    hasExpiredRef.current = true;
    onExpire(item.id);
  }, [isExpired, item.id, onExpire]);

  return (
    <ControlledShow data={item}>
      <Box component="article" sx={CARD_SX}>
        <InterruptionsEmergencyCardHeader
          direction={item.direction}
          isUrgentTimer={isUrgentTimer}
          ttlLabel={ttlLabel}
        />
        <InterruptionCreatorField
          creatorIdSource="targetUserId"
          creatorNameSource="targetUserName"
          onOpenChat={openExternalLink}
        />
        <InterruptionMessage />
        <InterruptionsEmergencyCardActions
          expired={isExpired}
          item={item}
          id={item.id}
          onAccept={id => onAccept(id, item.teamsCallLink)}
          onCancel={onCancel}
          onDismiss={onDismiss}
          onPostpone={onPostpone}
        />
      </Box>
    </ControlledShow>
  );
};

export default InterruptionsEmergencyCard;
